import { cn } from '@/lib/utils';

interface BudgetProgressBarProps {
  total: number;
  actual: number;
  label?: string;
  showValues?: boolean;
  size?: 'sm' | 'md';
  warningPercent?: number;
  criticalPercent?: number;
}

export function BudgetProgressBar({
  total,
  actual,
  label,
  showValues = true,
  size = 'md',
  warningPercent = 80,
  criticalPercent = 100,
}: BudgetProgressBarProps) {
  const percent = total > 0 ? Math.round((actual / total) * 100) : 0;
  const clampedWidth = Math.min(percent, 100);

  const getColor = () => {
    if (percent >= criticalPercent) return 'bg-danger';
    if (percent >= warningPercent) return 'bg-warning';
    return 'bg-success';
  };

  const getTextColor = () => {
    if (percent >= criticalPercent) return 'text-danger';
    if (percent >= warningPercent) return 'text-warning';
    return 'text-success';
  };

  const fmt = (v: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

  return (
    <div className="w-full">
      {(label || showValues) && (
        <div className="mb-1 flex items-center justify-between">
          {label && <span className="text-xs font-medium text-muted">{label}</span>}
          {showValues && (
            <span className={cn('text-xs font-semibold', getTextColor())}>
              {fmt(actual)} / {fmt(total)} ({percent}%)
            </span>
          )}
        </div>
      )}
      <div className={cn('w-full overflow-hidden rounded-full bg-card-hover', size === 'sm' ? 'h-1.5' : 'h-2.5')}>
        <div
          className={cn('h-full rounded-full transition-all duration-500', getColor())}
          style={{ width: `${clampedWidth}%` }}
        />
      </div>
    </div>
  );
}

interface BudgetHealthIndicatorProps {
  level: 'green' | 'yellow' | 'red';
  label: string;
  percent: number;
  onClick?: () => void;
}

export function BudgetHealthIndicator({ level, label, percent, onClick }: BudgetHealthIndicatorProps) {
  const colors = {
    green: 'bg-success-soft text-success',
    yellow: 'bg-warning-soft text-warning',
    red: 'bg-danger-soft text-danger',
  };

  const dots = {
    green: 'bg-success',
    yellow: 'bg-warning',
    red: 'bg-danger',
  };

  return (
    <button
      onClick={onClick}
      className={cn('flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors', colors[level], onClick && 'cursor-pointer hover:opacity-80')}
    >
      <span className={cn('h-2 w-2 rounded-full', dots[level])} />
      <span className="truncate">{label}</span>
      <span className="ml-auto font-bold">{percent}%</span>
    </button>
  );
}
